/**
 * Scorecard — a reusable weighted bundle of Judges. Attaching a Scorecard
 * to an Evaluation is shorthand for "attach all member judges and roll
 * their per-entry verdicts into a single 0..1 trait score."
 *
 * Each judge's output normalizes to 0..1 like so:
 *   - boolean → true = 1, false = 0
 *   - number  → value, clamped to [0, 1]
 *   - enum    → looked up in ScorecardItem.enumValueMap (author-supplied);
 *               values not in the map contribute 0
 *
 * Final trait score = sum(weight × normalized) / sum(weight).
 */
import { z } from 'zod';
/** Authored mapping for enum-typed scores: each enum value → 0..1. */
export const EnumValueMapSchema = z.record(z.string(), z.number().min(0).max(1)).nullable();
export const ScorecardItemSchema = z.object({
    id: z.string(),
    scorecardId: z.string(),
    judgeId: z.string(),
    weight: z.number().nonnegative(),
    enumValueMap: EnumValueMapSchema,
    createdAt: z.string().datetime(),
});
export const ScorecardSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /** When set, an entry's trait score crossing this threshold counts as passing. */
    passThreshold: z.number().min(0).max(1).nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /** Hydrated by the API when listing — empty when items aren't included. */
    items: z.array(ScorecardItemSchema).default([]),
});
// ─── Inputs ─────────────────────────────────────────────────────────────────
/** Item shape for create/update bodies (no id; FK omitted — set from URL). */
export const ScorecardItemInputSchema = z.object({
    judgeId: z.string().min(1),
    weight: z.number().nonnegative().default(1),
    enumValueMap: EnumValueMapSchema.optional(),
});
export const CreateScorecardInputSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    passThreshold: z.number().min(0).max(1).nullable().optional(),
    items: z.array(ScorecardItemInputSchema).default([]),
});
/**
 * Partial update. `items` (when supplied) replaces the full set — easier
 * to reason about than per-item add/remove diffs and the table is small.
 */
export const UpdateScorecardInputSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(2000).nullable().optional(),
    passThreshold: z.number().min(0).max(1).nullable().optional(),
    items: z.array(ScorecardItemInputSchema).optional(),
});
// ─── Per-entry runtime output ───────────────────────────────────────────────
/**
 * One scorecard's rolled-up verdict for a single ground-truth entry. The
 * eval-harness emits this alongside the existing `judgeScores` on each
 * IEntryResult so the UI can render scorecard summaries without
 * recomputing from JudgeRun rows.
 */
export const ScorecardEntryScoreSchema = z.object({
    scorecardId: z.string(),
    scorecardName: z.string(),
    /** Weighted 0..1 score. NaN if no members produced a usable value. */
    score: z.number(),
    /** When the scorecard has a passThreshold, whether `score >= threshold`. */
    passed: z.boolean().nullable(),
    /** Per-member trail: how each judge's value contributed to the score. */
    contributions: z.array(z.object({
        judgeId: z.string(),
        judgeName: z.string(),
        scoreName: z.string(),
        rawValue: z.union([z.boolean(), z.number(), z.string()]).nullable(),
        normalized: z.number().nullable(),
        weight: z.number(),
    })),
});
/**
 * Body for `PUT /v1/evaluations/:id/scorecards/:scorecardId`. Idempotent —
 * mirrors the per-judge attach endpoint's shape so both surfaces match.
 */
export const UpsertEvaluationScorecardInputSchema = z.object({
    enabled: z.boolean().optional(),
});
/** Output of `GET /v1/evaluations/:id/scorecards` — flattened attachment row. */
export const EvaluationScorecardAttachmentSchema = z.object({
    evaluationId: z.string(),
    scorecardId: z.string(),
    scorecardName: z.string(),
    scorecardDescription: z.string().nullable(),
    enabled: z.boolean(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=scorecard.js.map